import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchCustomerPortalCategoryServices } from "../services/customerPortalBooking.service";

const keyOf = (categoryId) => String(categoryId);

/**
 * Loads services for one category. Skips network if already cached or already in-flight (condition).
 */
export const ensureCustomerPortalCategoryServices = createAsyncThunk(
  "customerPortalBooking/ensureCategoryServices",
  async ({ token, categoryId }, { rejectWithValue }) => {
    try {
      const { data } = await fetchCustomerPortalCategoryServices(token, categoryId);
      const list = Array.isArray(data?.data) ? data.data : [];
      return { categoryId, services: list };
    } catch (err) {
      return rejectWithValue({ categoryId, message: err?.message || "Failed to load services" });
    }
  },
  {
    condition: ({ categoryId }, { getState }) => {
      if (categoryId == null || categoryId === "") return false;
      const state = getState().customerPortalBooking;
      if (!state) return true;
      const k = keyOf(categoryId);
      if (state.servicesByCategoryId[k] !== undefined) return false;
      if (state.loadingIds[k]) return false;
      return true;
    },
  }
);

const initialState = {
  /** key: String(categoryId) → Service[] (empty array = loaded, no services) */
  servicesByCategoryId: {},
  loadingIds: {},
};

const slice = createSlice({
  name: "customerPortalBooking",
  initialState,
  reducers: {
    clearCustomerPortalBookingServices(state) {
      state.servicesByCategoryId = {};
      state.loadingIds = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ensureCustomerPortalCategoryServices.pending, (state, action) => {
        const k = keyOf(action.meta.arg.categoryId);
        state.loadingIds[k] = true;
      })
      .addCase(ensureCustomerPortalCategoryServices.fulfilled, (state, action) => {
        const k = keyOf(action.payload.categoryId);
        state.servicesByCategoryId[k] = action.payload.services;
        delete state.loadingIds[k];
      })
      .addCase(ensureCustomerPortalCategoryServices.rejected, (state, action) => {
        const catId = action.meta?.arg?.categoryId;
        if (catId == null) return;
        const k = keyOf(catId);
        state.servicesByCategoryId[k] = [];
        delete state.loadingIds[k];
      });
  },
});

export const { clearCustomerPortalBookingServices } = slice.actions;
export default slice.reducer;

export function selectCustomerPortalBookingServicesMap(state) {
  return state.customerPortalBooking?.servicesByCategoryId ?? {};
}

export function selectCustomerPortalCategoryServices(state, categoryId) {
  if (categoryId == null) return undefined;
  return state.customerPortalBooking?.servicesByCategoryId[keyOf(categoryId)];
}

export function selectCustomerPortalCategoryServicesLoading(state, categoryId) {
  if (categoryId == null) return false;
  return Boolean(state.customerPortalBooking?.loadingIds[keyOf(categoryId)]);
}
